import { useState, useEffect } from 'react'
import {
  X, User, Mail, Phone, MapPin, Truck, FileText, CreditCard,
  Package, ChevronDown, Loader2, Pencil, MoreVertical, Box, Save, AlertTriangle
} from 'lucide-react'
import { QueueItem, shipping } from '@/lib/api'
import { CountryFlag } from '@/components/CarrierLogos'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { useToast } from '@/components/ui/Toast'

// Lista de países comunes
const COUNTRIES = [
  { code: 'ES', name: 'España' },
  { code: 'PT', name: 'Portugal' },
  { code: 'FR', name: 'Francia' },
  { code: 'DE', name: 'Alemania' },
  { code: 'IT', name: 'Italia' },
  { code: 'GB', name: 'Reino Unido' },
  { code: 'BE', name: 'Bélgica' },
  { code: 'NL', name: 'Países Bajos' },
  { code: 'AT', name: 'Austria' },
  { code: 'PL', name: 'Polonia' },
  { code: 'IE', name: 'Irlanda' },
  { code: 'FI', name: 'Finlandia' },
  { code: 'SE', name: 'Suecia' },
  { code: 'DK', name: 'Dinamarca' },
  { code: 'NO', name: 'Noruega' },
  { code: 'CH', name: 'Suiza' },
  { code: 'GR', name: 'Grecia' },
  { code: 'CZ', name: 'República Checa' },
  { code: 'HU', name: 'Hungría' },
  { code: 'RO', name: 'Rumanía' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croacia' },
  { code: 'SK', name: 'Eslovaquia' },
  { code: 'SI', name: 'Eslovenia' },
  { code: 'LT', name: 'Lituania' },
  { code: 'LV', name: 'Letonia' },
  { code: 'EE', name: 'Estonia' },
  { code: 'CY', name: 'Chipre' },
  { code: 'MT', name: 'Malta' },
  { code: 'LU', name: 'Luxemburgo' },
]

// Lista de transportistas
const CARRIERS = [
  { code: 'mrw', name: 'MRW' },
  { code: 'gls', name: 'GLS' },
  { code: 'ctt', name: 'CTT Express' },
  { code: 'cex', name: 'Correos Express' },
  { code: 'fedex', name: 'FedEx (UE sin ES/PT)' },
]

// Modal de edición de datos de entrega
interface EditDeliveryModalProps {
  item: QueueItem
  onClose: () => void
  onSave: (data: EditableDeliveryData) => void
  isSaving: boolean
}

interface EditableDeliveryData {
  // Datos del cliente
  name: string
  company: string
  email: string
  phone: string
  phone_mobile: string
  mobile_ok?: boolean | null
  // Dirección
  street: string
  street2: string
  city: string
  postal_code: string
  state: string
  country_code: string
  // Envío
  carrier_code: string
  packages: number
  weight: number
  is_cod_enabled: boolean  // Toggle "Contra reembolso (COD)" del modal
  cod_amount: number  // Importe contrareembolso (0 = no es COD)
  observations: string  // Observaciones para el envío
  with_return: boolean  // Envío con retorno (el mensajero lleva etiqueta de retorno)
}

// Mensaje automático para envíos COD a Portugal
const COD_PORTUGAL_MESSAGE = "NAO ACEITAMOS CHEQUES. OBRIGADO."

function EditDeliveryModal({ item, onClose, onSave, isSaving }: EditDeliveryModalProps) {
  // Determinar observaciones iniciales (desde carrier_notes guardado en cola)
  const getInitialObservations = () => {
    const base = item.carrier_notes || ''
    const isPortugal = item.shipping_address?.country_code === 'PT'
    const isCod = (item.cod_amount || 0) > 0
    if (isPortugal && isCod && !base.includes(COD_PORTUGAL_MESSAGE)) {
      return base ? `${base}. ${COD_PORTUGAL_MESSAGE}` : COD_PORTUGAL_MESSAGE
    }
    return base
  }

  const [formData, setFormData] = useState<EditableDeliveryData>({
    name: item.shipping_address?.name || item.customer_name || '',
    company: item.shipping_address?.company || '',
    email: item.shipping_address?.email || '',
    phone: item.shipping_address?.phone || '',
    phone_mobile: item.shipping_address?.phone_mobile || '',
    street: item.shipping_address?.street || '',
    street2: item.shipping_address?.street2 || '',
    city: item.shipping_address?.city || '',
    postal_code: item.shipping_address?.postal_code || '',
    state: item.shipping_address?.state || '',
    country_code: item.shipping_address?.country_code || 'ES',
    carrier_code: item.carrier_code || item.suggested_carrier?.carrier_code || 'mrw',
    packages: item.packages || 1,
    weight: item.weight || 0.5,
    is_cod_enabled: !!item.is_cod || (item.cod_amount || 0) > 0,
    cod_amount: item.cod_amount || 0,
    observations: getInitialObservations(),
    with_return: item.with_return ?? false,
  })

  // Recordamos el último importe COD válido para poder restaurarlo si el operador
  // desactiva y vuelve a activar el toggle sin recargar el modal.
  const [lastCodAmount, setLastCodAmount] = useState<number>(item.cod_amount || 0)

  const handleChange = (field: keyof EditableDeliveryData, value: string | number) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value }

      // Auto-actualizar observaciones si cambia país o COD
      if (field === 'country_code' || field === 'cod_amount') {
        const isPortugal = field === 'country_code' ? value === 'PT' : prev.country_code === 'PT'
        const isCod = field === 'cod_amount' ? (value as number) > 0 : prev.cod_amount > 0

        if (isPortugal && isCod) {
          // Añadir mensaje si no está ya
          if (!newData.observations.includes(COD_PORTUGAL_MESSAGE)) {
            newData.observations = newData.observations
              ? `${newData.observations}. ${COD_PORTUGAL_MESSAGE}`
              : COD_PORTUGAL_MESSAGE
          }
        }
      }

      return newData
    })
  }

  // Validar restricción de bultos por transportista y destino
  const getPackageLimitWarning = (): string | null => {
    const carrier = formData.carrier_code?.toLowerCase()
    const country = formData.country_code
    const packages = formData.packages

    // MRW: Solo 1 bulto para Portugal
    if (carrier === 'mrw' && country === 'PT' && packages > 1) {
      return `MRW solo permite 1 bulto para envíos a Portugal. Reduce a 1 bulto o cambia de transportista.`
    }

    // GLS: Solo 1 bulto para envíos internacionales (excepto Portugal)
    if (carrier === 'gls' && country !== 'ES' && country !== 'PT' && packages > 1) {
      return `GLS solo permite 1 bulto para envíos internacionales. Reduce a 1 bulto o cambia de transportista.`
    }

    return null
  }

  const packageWarning = getPackageLimitWarning()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (packageWarning) {
      return // No permitir guardar si hay error de bultos
    }
    onSave(formData)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 dark:bg-teal-900/50 rounded-lg">
              <Pencil className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Editar datos de entrega
              </h2>
              <p className="text-sm text-slate-500">
                Pedido #{item.order_id} - {item.order_reference}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Datos del destinatario */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <User className="w-4 h-4" />
                Datos del destinatario
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Empresa
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Móvil *
                    </label>
                    <input
                      type="tel"
                      value={formData.phone_mobile}
                      onChange={(e) => handleChange('phone_mobile', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Dirección de entrega */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Dirección de entrega
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Dirección *
                  </label>
                  <input
                    type="text"
                    value={formData.street}
                    onChange={(e) => handleChange('street', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Calle, número, piso..."
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Dirección línea 2
                  </label>
                  <input
                    type="text"
                    value={formData.street2}
                    onChange={(e) => handleChange('street2', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Edificio, portal, escalera..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Ciudad *
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Código postal *
                  </label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => handleChange('postal_code', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Provincia / Estado
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    País *
                  </label>
                  <div className="relative">
                    <select
                      value={formData.country_code}
                      onChange={(e) => handleChange('country_code', e.target.value)}
                      className="w-full px-3 py-2 pl-10 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none"
                      required
                    >
                      {COUNTRIES.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <CountryFlag countryCode={formData.country_code} className="w-5 h-4" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Datos del envío */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Datos del envío
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Transportista
                  </label>
                  <select
                    value={formData.carrier_code}
                    onChange={(e) => handleChange('carrier_code', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    {CARRIERS.map(carrier => (
                      <option key={carrier.code} value={carrier.code}>
                        {carrier.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Nº de bultos
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.packages}
                    onChange={(e) => handleChange('packages', parseInt(e.target.value) || 1)}
                    className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      packageWarning
                        ? 'border-red-500 dark:border-red-500'
                        : 'border-slate-300 dark:border-slate-600'
                    }`}
                  />
                  {packageWarning && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <span className="font-medium">⚠</span> {packageWarning}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Peso (kg)
                  </label>
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={formData.weight}
                    onChange={(e) => handleChange('weight', parseFloat(e.target.value) || 0.5)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Forma de pago
                  </label>
                  <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_cod_enabled}
                      onChange={(e) => {
                        const enabled = e.target.checked
                        setFormData(prev => ({
                          ...prev,
                          is_cod_enabled: enabled,
                          cod_amount: enabled ? (lastCodAmount > 0 ? lastCodAmount : prev.cod_amount) : 0,
                        }))
                      }}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      Contra reembolso (COD)
                    </span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.cod_amount}
                    disabled={!formData.is_cod_enabled}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0
                      if (val > 0) setLastCodAmount(val)
                      handleChange('cod_amount', val)
                    }}
                    placeholder={formData.is_cod_enabled ? 'Importe a cobrar (€)' : 'Sin contrareembolso'}
                    className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-slate-100 disabled:dark:bg-slate-800 disabled:text-slate-400 disabled:cursor-not-allowed"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.is_cod_enabled
                      ? 'Importe que el transportista cobrará en la entrega'
                      : 'Desactiva el reembolso si el cliente ya pagó por otro medio'}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Observaciones
                  </label>
                  <textarea
                    value={formData.observations}
                    onChange={(e) => handleChange('observations', e.target.value)}
                    rows={2}
                    placeholder="Observaciones para el transportista..."
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  />
                  {formData.country_code === 'PT' && formData.cod_amount > 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      ⚠ Envío COD a Portugal: se recomienda incluir mensaje de no aceptar cheques
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.with_return}
                      onChange={(e) => setFormData(prev => ({ ...prev, with_return: e.target.checked }))}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    <div>
                      <span className="text-sm text-slate-700 dark:text-slate-300">Con retorno</span>
                      <p className="text-xs text-slate-500">El mensajero lleva etiqueta de retorno (MRW / GLS / CTT)</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Footer con botones */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving || !!packageWarning}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Guardar cambios
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

interface QueueOrderDetailProps {
  item: QueueItem
  onClose: () => void
  onLabelGenerated?: () => void
}

// Logo del transportista
function CarrierLogo({ carrierCode }: { carrierCode: string }) {
  const carriers: Record<string, { name: string; bgColor: string; textColor: string; borderColor: string }> = {
    mrw: {
      name: 'MRW',
      bgColor: 'bg-red-600',
      textColor: 'text-white',
      borderColor: 'border-red-700'
    },
    gls: {
      name: 'GLS',
      bgColor: 'bg-yellow-400',
      textColor: 'text-yellow-900',
      borderColor: 'border-yellow-500'
    },
    ctt: {
      name: 'CTT',
      bgColor: 'bg-blue-600',
      textColor: 'text-white',
      borderColor: 'border-blue-700'
    }
  }

  const carrier = carriers[carrierCode?.toLowerCase()] || {
    name: carrierCode?.toUpperCase() || 'N/A',
    bgColor: 'bg-slate-500',
    textColor: 'text-white',
    borderColor: 'border-slate-600'
  }

  return (
    <div className={`px-6 py-3 rounded-lg border-2 ${carrier.bgColor} ${carrier.borderColor} flex items-center justify-center shadow-sm`}>
      <span className={`text-xl font-black tracking-wide ${carrier.textColor}`}>{carrier.name}</span>
    </div>
  )
}

// Nombre del servicio legible
function getServiceName(carrierCode: string, serviceType: string): string {
  const services: Record<string, Record<string, string>> = {
    mrw: {
      '0800': 'Sin Tramo Horario',
      '0800_0814': 'Entrega 8:00-14:00',
      '0800_1619': 'Entrega 16:00-19:00',
      'canje': 'Canje'
    },
    gls: {
      'business_parcel': 'Business Parcel',
      'eurobusiness_small_parcel': 'Eurobusiness Small Parcel'
    },
    ctt: {
      '24H': '24 Horas',
      'baleares': 'Baleares Economy',
      'premium': 'Premium Empresas'
    }
  }

  return services[carrierCode?.toLowerCase()]?.[serviceType] || serviceType || ''
}

export default function QueueOrderDetail({ item, onClose, onLabelGenerated }: QueueOrderDetailProps) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [showEditModal, setShowEditModal] = useState(false)
  const [showBillingAddress, setShowBillingAddress] = useState(false)
  const selectedPackageSize = 'small' // Tamaño por defecto para nuevos paquetes

  // Estados para gestión de paquetes estilo Outvio
  // Usamos array para mantener el orden de selección (el último es donde aparece "Cerrar paquete")
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([])
  const [isCreatingPackage, setIsCreatingPackage] = useState(false)
  const [showOptionsMenu, setShowOptionsMenu] = useState(false)

  // Estado para datos editables (actualizados después de guardar)
  const [localItem, setLocalItem] = useState(item)

  // Estado para observaciones (desde carrier_notes guardado en cola)
  const getInitialObservations = () => {
    const base = item.carrier_notes || ''
    const isPortugal = item.shipping_address?.country_code === 'PT'
    const isCod = (item.cod_amount || 0) > 0
    if (isPortugal && isCod && !base.includes(COD_PORTUGAL_MESSAGE)) {
      return base ? `${base}. ${COD_PORTUGAL_MESSAGE}` : COD_PORTUGAL_MESSAGE
    }
    return base
  }
  const [observations, setObservations] = useState(getInitialObservations)

  // Estado para edición inline del COD
  const [isEditingCod, setIsEditingCod] = useState(false)
  const [tempCodAmount, setTempCodAmount] = useState(localItem.cod_amount || 0)

  // Estado para edición inline del transportista
  const [isEditingCarrier, setIsEditingCarrier] = useState(false)
  const [tempCarrierCode, setTempCarrierCode] = useState(localItem.carrier_code || localItem.suggested_carrier?.carrier_code || 'mrw')

  // Estado para tramo horario MRW (1=mañana, 2=tarde)
  const [mrwTimeSlot, setMrwTimeSlot] = useState<'1' | '2'>('1')
  const [tempMrwTimeSlot, setTempMrwTimeSlot] = useState<'1' | '2'>('1')

  // Estado para envío con retorno (se hereda del pedido manual si se marcó al crearlo;
  // por defecto desmarcado, es de uso puntual)
  const [withReturn, setWithReturn] = useState(item.with_return ?? false)

  // Resincronizar el retorno al cambiar de pedido seleccionado (el componente no se remonta)
  useEffect(() => {
    setWithReturn(item.with_return ?? false)
  }, [item.order_id, item.with_return])

  // Query para obtener tamaños de paquete disponibles
  const { data: packageSizesData } = useQuery({
    queryKey: ['package-sizes'],
    queryFn: () => shipping.getPackageSizes(),
    staleTime: 1000 * 60 * 60, // 1 hora
  })

  // Query para obtener paquetes del pedido
  const { data: packagesData, refetch: refetchPackages } = useQuery({
    queryKey: ['order-packages', item.order_id],
    queryFn: () => shipping.getOrderPackages(item.order_id),
  })

  const packages = packagesData?.packages || []
  const availablePackageSizes = packageSizesData?.sizes || []

  // Obtener productos no empaquetados
  const getUnpackagedProducts = () => {
    if (!localItem.products) return []
    const packedProductIds = new Set(
      packages.flatMap(pkg => pkg.items.map(item => item.product_id))
    )
    return localItem.products.filter(p => !packedProductIds.has(p.id))
  }

  const unpackagedProducts = getUnpackagedProducts()
  const packagesCount = localItem.packages || 1
  const weight = localItem.weight || 1.0
  const carrierCode = localItem.carrier_code || localItem.suggested_carrier?.carrier_code || 'mrw'
  const countryCode = localItem.shipping_address?.country_code || 'ES'

  // Validar restricción de bultos por transportista y destino
  const getMainPackageLimitWarning = (): string | null => {
    // MRW: Solo 1 bulto para Portugal
    if (carrierCode === 'mrw' && countryCode === 'PT' && packagesCount > 1) {
      return `MRW solo permite 1 bulto para Portugal`
    }
    // GLS: Solo 1 bulto para envíos internacionales (excepto Portugal)
    if (carrierCode === 'gls' && countryCode !== 'ES' && countryCode !== 'PT' && packagesCount > 1) {
      return `GLS solo permite 1 bulto para envíos internacionales`
    }
    return null
  }

  const mainPackageWarning = getMainPackageLimitWarning()

  // Query para calcular el coste de envío real
  const shippingCostQuery = useQuery({
    queryKey: ['shipping-cost-calc', carrierCode, localItem.shipping_address?.country_code, weight],
    queryFn: () => shipping.calculateShippingCost({
      carrier_code: carrierCode,
      country_code: localItem.shipping_address?.country_code || 'ES',
      weight: weight
    }),
    enabled: !!carrierCode,
    staleTime: 30000,
  })

  // Mutation para actualizar datos de envío en el backend
  const updateDeliveryMutation = useMutation({
    mutationFn: async (data: EditableDeliveryData) => {
      // Actualizar carrier_code, packages, weight, cod_amount y carrier_notes en el backend
      await shipping.updateQueueItem(item.order_id, {
        carrier_code: data.carrier_code,
        packages: data.packages,
        weight: data.weight,
        cod_amount: data.cod_amount,
        carrier_notes: data.observations,
        with_return: data.with_return,
      })
      return data // Devolver los datos completos para actualizar el estado local
    },
    onSuccess: (data) => {
      toast.success('Datos de entrega actualizados')
      // Actualizar estado local con TODOS los datos (incluida la dirección)
      setLocalItem(prev => ({
        ...prev,
        carrier_code: data.carrier_code,
        packages: data.packages,
        weight: data.weight,
        cod_amount: data.cod_amount,
        is_cod: data.cod_amount > 0,
        with_return: data.with_return,
        customer_name: data.name,
        shipping_address: {
          name: data.name,
          company: data.company,
          email: data.email,
          phone: data.phone,
          phone_mobile: data.phone_mobile,
          street: data.street,
          street2: data.street2,
          city: data.city,
          postal_code: data.postal_code,
          state: data.state,
          country_code: data.country_code,
        }
      }))
      // Actualizar observaciones
      setObservations(data.observations)
      // Sincronizar el checkbox inline "Con retorno" con lo guardado en el modal
      setWithReturn(data.with_return)
      queryClient.invalidateQueries({ queryKey: ['shipping-queue'] })
      setShowEditModal(false)
    },
    onError: (error: any) => {
      toast.error('Error al actualizar', error.response?.data?.detail || error.message)
    }
  })

  // Mutation para actualizar solo el COD amount
  const updateCodMutation = useMutation({
    mutationFn: async (newCodAmount: number) => {
      await shipping.updateQueueItem(item.order_id, {
        cod_amount: newCodAmount,
      })
      return newCodAmount
    },
    onSuccess: (newCodAmount) => {
      toast.success('Importe de reembolso actualizado')
      setLocalItem(prev => ({
        ...prev,
        cod_amount: newCodAmount,
        is_cod: newCodAmount > 0,
      }))
      // Actualizar observaciones si es Portugal y ahora es COD
      const isPortugal = localItem.shipping_address?.country_code === 'PT'
      if (isPortugal && newCodAmount > 0 && !observations.includes(COD_PORTUGAL_MESSAGE)) {
        setObservations(prev => prev ? `${prev}. ${COD_PORTUGAL_MESSAGE}` : COD_PORTUGAL_MESSAGE)
      }
      queryClient.invalidateQueries({ queryKey: ['shipping-queue'] })
      setIsEditingCod(false)
    },
    onError: (error: any) => {
      toast.error('Error al actualizar reembolso', error.response?.data?.detail || error.message)
    }
  })

  // Mutation para actualizar el transportista
  const updateCarrierMutation = useMutation({
    mutationFn: async (newCarrierCode: string) => {
      await shipping.updateQueueItem(item.order_id, {
        carrier_code: newCarrierCode,
      })
      return newCarrierCode
    },
    onSuccess: (newCarrierCode) => {
      toast.success(`Transportista cambiado a ${newCarrierCode.toUpperCase()}`)
      setLocalItem(prev => ({
        ...prev,
        carrier_code: newCarrierCode,
      }))
      queryClient.invalidateQueries({ queryKey: ['shipping-queue'] })
      setIsEditingCarrier(false)
    },
    onError: (error: any) => {
      toast.error('Error al cambiar transportista', error.response?.data?.detail || error.message)
    }
  })

  // Mutation para persistir el flag "con retorno" en la cola
  const updateReturnMutation = useMutation({
    mutationFn: async (newWithReturn: boolean) => {
      await shipping.updateQueueItem(item.order_id, {
        with_return: newWithReturn,
      })
      return newWithReturn
    },
    onSuccess: (newWithReturn) => {
      toast.success(newWithReturn ? 'Envío con retorno activado' : 'Envío con retorno desactivado')
      setLocalItem(prev => ({ ...prev, with_return: newWithReturn }))
      queryClient.invalidateQueries({ queryKey: ['shipping-queue'] })
    },
    onError: (error: any, newWithReturn) => {
      // Revertir el checkbox al valor previo si falla la persistencia
      setWithReturn(!newWithReturn)
      toast.error('Error al guardar retorno', error.response?.data?.detail || error.message)
    }
  })

  // Mutation para generar etiqueta
  const generateLabelMutation = useMutation({
    mutationFn: async () => {
      // Determinar service_type según transportista y opciones
      let serviceType: string | undefined = undefined
      if (carrierCode === 'mrw') {
        // MRW: usar tramo horario seleccionado
        serviceType = mrwTimeSlot === '2' ? 'ecommerce_1400_1900' : 'ecommerce_0830_1400'
      } else if (carrierCode === 'dhl' && item.service_point) {
        // DHL: si el pedido tiene service point seleccionado, enviar a punto de recogida
        serviceType = 'service_point'
      }

      // Auto-guardar carrier_notes si difiere del valor guardado
      if (observations !== (item.carrier_notes || '')) {
        await shipping.updateQueueItem(item.order_id, {
          carrier_notes: observations || '',
        })
      }

      // Crear el envío (el backend elimina de la cola automáticamente)
      return await shipping.createShipment({
        order_id: item.order_id,
        carrier_code: carrierCode,
        service_type: serviceType,
        packages: packagesCount,
        weight: weight,
        notes: observations ?? undefined,  // Observaciones para la etiqueta
        test_mode: false,
        with_return: withReturn  // Envío con retorno
      })
    },
    onSuccess: (data) => {
      toast.success(`Etiqueta generada: ${data.tracking_number || 'N/A'}`)
      // Refrescar listas
      queryClient.invalidateQueries({ queryKey: ['shipping-queue'] })
      queryClient.invalidateQueries({ queryKey: ['shipping-queue-ids'] })
      queryClient.invalidateQueries({ queryKey: ['shipping-tracking'] })
      onLabelGenerated?.()
      onClose()
    },
    onError: (error: any) => {
      toast.error('Error al generar etiqueta', error.response?.data?.detail || error.message)
    }
  })

  // Mutation para desempaquetar (eliminar paquete y liberar productos)
  const unpackMutation = useMutation({
    mutationFn: async (packageId: number) => {
      // Primero desempaquetar (liberar productos)
      const result = await shipping.unpackPackage(item.order_id, packageId)
      // Luego eliminar el paquete vacío
      await shipping.deletePackage(item.order_id, packageId)
      return result
    },
    onSuccess: (data) => {
      toast.success(`${data.removed_items} productos desempaquetados`)
      refetchPackages()
    },
    onError: (error: any) => {
      toast.error('Error al desempaquetar', error.response?.data?.detail || error.message)
    }
  })

  // Mutation para cancelar envío (eliminar de cola y volver a pedidos)
  const cancelShipmentMutation = useMutation({
    mutationFn: async () => {
      // Eliminar todos los paquetes del pedido
      for (const pkg of packages) {
        if (pkg.id) {
          await shipping.deletePackage(item.order_id, pkg.id)
        }
      }
      // Eliminar de la cola de envío
      await shipping.removeFromQueue(item.order_id)
    },
    onSuccess: () => {
      toast.success('Envío cancelado - el pedido vuelve al listado de pedidos')
      queryClient.invalidateQueries({ queryKey: ['shipping-queue'] })
      queryClient.invalidateQueries({ queryKey: ['shipping-orders'] })
      onClose()
    },
    onError: (error: any) => {
      toast.error('Error al cancelar envío', error.response?.data?.detail || error.message)
    }
  })

  // Toggle selección de producto (mantiene orden para saber cuál es el último)
  const toggleProductSelection = (productId: number) => {
    setSelectedProductIds(prev => {
      if (prev.includes(productId)) {
        // Quitar de la selección
        return prev.filter(id => id !== productId)
      } else {
        // Añadir al final (será el último seleccionado)
        return [...prev, productId]
      }
    })
  }

  // Crear paquete con productos seleccionados
  const handleCreatePackageWithSelected = async () => {
    if (selectedProductIds.length === 0) {
      toast.error('Selecciona al menos un producto')
      return
    }

    setIsCreatingPackage(true)
    try {
      // Crear paquete
      const result = await shipping.createPackage(item.order_id, {
        size_code: selectedPackageSize,
        weight: 0.5
      })

      if (result.package_id) {
        const productsToAdd = localItem.products
          ?.filter(p => selectedProductIds.includes(p.id))
          .map(p => ({
            product_id: p.id,
            product_name: p.name,
            product_reference: p.reference,
            quantity: p.quantity,
            price: p.price,
            image_url: p.image_url
          })) || []

        await shipping.closePackage(item.order_id, result.package_id, productsToAdd)
        setSelectedProductIds([])
        refetchPackages()
        toast.success('Paquete creado')
      }
    } catch (error: any) {
      toast.error('Error al crear paquete', error.message)
    } finally {
      setIsCreatingPackage(false)
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return {
      time: date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
      date: date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
    }
  }

  const { time, date } = item.added_at ? formatDate(item.added_at) : { time: '--:--', date: '--/--/----' }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Header - Barra horizontal con info del pedido */}
      <div className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
        {/* Columnas del header */}
        <div className="grid grid-cols-[80px_100px_120px_80px_80px_60px_150px_40px] gap-2 px-4 py-2 text-[10px] font-medium text-teal-600 dark:text-teal-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700">
          <div>Fuente</div>
          <div>Fecha</div>
          <div>ID del pedido</div>
          <div>Destino</div>
          <div>Total</div>
          <div>Productos</div>
          <div>Estado</div>
          <div></div>
        </div>

        {/* Valores */}
        <div className="grid grid-cols-[80px_100px_120px_80px_80px_60px_150px_40px] gap-2 px-4 py-3 items-center text-sm">
          {/* Fuente - Logo PrestaShop */}
          <div>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-[10px]">PS</span>
            </div>
          </div>

          {/* Fecha */}
          <div className="text-slate-600 dark:text-slate-400">
            <div className="font-medium">{time}</div>
            <div className="text-xs">{date}</div>
          </div>

          {/* ID Pedido */}
          <div>
            <div className="font-semibold text-slate-900 dark:text-white">{item.order_id}</div>
            <div className="text-xs text-slate-500">{item.order_reference}</div>
          </div>

          {/* Destino */}
          <div className="flex items-center gap-1.5">
            <CountryFlag countryCode={localItem.shipping_address?.country_code || 'ES'} className="w-5 h-4" />
            <span className="text-slate-600 dark:text-slate-400 text-xs">
              {localItem.shipping_address?.country_code || 'ES'}
            </span>
          </div>

          {/* Total */}
          <div className="font-semibold text-slate-900 dark:text-white">
            €{localItem.total?.toFixed(2) || '0.00'}
          </div>

          {/* Productos */}
          <div className="text-center text-slate-600 dark:text-slate-400">
            {localItem.items_count || 0}
          </div>

          {/* Estado */}
          <div>
            <span className="px-2 py-1 text-[10px] font-semibold rounded bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400">
              EN COLA
            </span>
          </div>

          {/* Cerrar */}
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="flex">
        {/* Columna principal */}
        <div className="flex-1 p-5">
          {/* Almacén y opciones */}
          <div className="flex items-center gap-6 mb-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Almacén de recogida:</span>
              <span className="font-medium text-slate-700 dark:text-slate-300">Default Warehouse</span>
            </div>
          </div>

          {/* Sección de paquetes estilo Outvio */}
          <div className="mb-6">
            {/* Header con botones */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {unpackagedProducts.length > 0
                  ? 'Selecciona productos que vayan en el mismo paquete:'
                  : packages.length > 0
                    ? `${packages.length} paquete${packages.length > 1 ? 's' : ''} preparado${packages.length > 1 ? 's' : ''} para envío`
                    : 'Cargando paquetes...'
                }
              </p>
              <div className="flex gap-2">
                {selectedProductIds.length > 0 ? (
                  <button
                    onClick={() => setSelectedProductIds([])}
                    className="px-3 py-1.5 bg-white dark:bg-slate-800 text-teal-600 dark:text-teal-400 border border-teal-200 dark:border-teal-700 rounded-lg text-sm font-medium hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-colors"
                  >
                    Deseleccionar todo
                  </button>
                ) : unpackagedProducts.length > 0 && (
                  <button
                    onClick={() => setSelectedProductIds(unpackagedProducts.map(p => p.id))}
                    className="px-3 py-1.5 bg-white dark:bg-slate-800 text-teal-600 dark:text-teal-400 border border-teal-200 dark:border-teal-700 rounded-lg text-sm font-medium hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-colors"
                  >
                    Seleccionar todo
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              {/* Paquetes cerrados existentes */}
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className="flex-1 min-w-[280px] max-w-sm p-4 bg-teal-50 dark:bg-teal-900/20 rounded-xl border-2 border-teal-200 dark:border-teal-700"
                >
                  {/* Header con selector de tamaño */}
                  <p className="text-[10px] font-semibold text-teal-700 dark:text-teal-400 uppercase tracking-wider mb-2">
                    Elige el tamaño del paquete:
                  </p>
                  <select
                    value={pkg.size_code}
                    onChange={(e) => {
                      const newSize = availablePackageSizes.find(s => s.code === e.target.value)
                      if (newSize) {
                        shipping.updatePackage(item.order_id, pkg.id!, {
                          size_code: newSize.code,
                          size_label: newSize.label
                        }).then(() => refetchPackages())
                      }
                    }}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-teal-200 dark:border-teal-700 bg-white dark:bg-slate-800 mb-3"
                  >
                    {(availablePackageSizes.length > 0 ? availablePackageSizes : [
                      { code: 'small', label: 'Small Box (20x20x10)' },
                      { code: 'medium', label: 'Medium Box (40x30x20)' },
                      { code: 'large', label: 'Large Box (60x40x30)' }
                    ]).map(size => (
                      <option key={size.code} value={size.code}>{size.label}</option>
                    ))}
                  </select>

                  {/* Grid de productos dentro del paquete con fotos */}
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-3 mb-3">
                    <div className="flex flex-wrap gap-2 justify-center mb-2">
                      {pkg.items.slice(0, 4).map((pkgItem, idx) => (
                        <div
                          key={pkgItem.id || idx}
                          className="relative w-14 h-14 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600"
                        >
                          {pkgItem.image_url ? (
                            <img
                              src={pkgItem.image_url}
                              alt={pkgItem.product_name}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <Package className={`w-6 h-6 text-slate-400 absolute inset-0 m-auto ${pkgItem.image_url ? 'hidden' : ''}`} />
                          {pkgItem.quantity > 1 && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-teal-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                              {pkgItem.quantity}
                            </span>
                          )}
                        </div>
                      ))}
                      {pkg.items.length > 4 && (
                        <div className="w-14 h-14 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-bold text-teal-600 dark:text-teal-400">
                            +{pkg.items.length - 4}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="text-center">
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                        {pkg.items.reduce((sum, i) => sum + i.quantity, 0)} PRODUCTO{pkg.items.reduce((sum, i) => sum + i.quantity, 0) !== 1 ? 'S' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Botón Desempaquetar */}
                  <button
                    onClick={() => unpackMutation.mutate(pkg.id!)}
                    disabled={unpackMutation.isPending}
                    className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium text-sm transition-colors"
                  >
                    {unpackMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Desempaquetar'
                    )}
                  </button>
                </div>
              ))}

              {/* Panel de envío */}
              <div className="flex-1 min-w-[240px] max-w-xs space-y-4">
                {/* Advertencia de límite de bultos */}
                {mainPackageWarning && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
                    <p className="text-sm text-red-700 dark:text-red-400 font-medium">
                      ⚠ {mainPackageWarning}
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-500 mt-1">
                      Edita los datos de entrega para corregir.
                    </p>
                  </div>
                )}

                <button
                  onClick={() => generateLabelMutation.mutate()}
                  disabled={generateLabelMutation.isPending || !!mainPackageWarning}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {generateLabelMutation.isPending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Package className="w-5 h-5" />
                  )}
                  Imprime etiqueta y envía
                </button>

                <p className="text-center text-sm font-medium">
                  <span className="text-slate-500 dark:text-slate-400">COSTE ENVÍO: </span>
                  {shippingCostQuery.isLoading ? (
                    <span className="text-slate-400">Calculando...</span>
                  ) : shippingCostQuery.data?.cost !== null && shippingCostQuery.data?.cost !== undefined ? (
                    <span className="text-teal-600 dark:text-teal-400">€{shippingCostQuery.data.cost.toFixed(2)}</span>
                  ) : (
                    <span className="text-amber-500" title="Configura los costes de envío en Logística → Costes de envío">
                      Sin configurar
                    </span>
                  )}
                </p>

                {/* Checkbox de envío con retorno */}
                <label className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                  <input
                    type="checkbox"
                    checked={withReturn}
                    disabled={updateReturnMutation.isPending}
                    onChange={(e) => {
                      const checked = e.target.checked
                      setWithReturn(checked)  // optimista
                      updateReturnMutation.mutate(checked)  // persistir en la cola
                    }}
                    className="w-4 h-4 text-teal-600 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-500 rounded focus:ring-teal-500 focus:ring-2"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Con retorno</span>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      El mensajero lleva etiqueta de retorno
                    </p>
                  </div>
                </label>

                {/* Indicador de contrareembolso (editable) */}
                {(localItem.is_cod || (localItem.cod_amount && localItem.cod_amount > 0)) && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                        <CreditCard className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase">Contrareembolso</span>
                      </div>
                      {!isEditingCod && (
                        <button
                          onClick={() => {
                            setTempCodAmount(localItem.cod_amount || 0)
                            setIsEditingCod(true)
                          }}
                          className="p-1 text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-800/50 rounded transition-colors"
                          title="Editar importe"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {isEditingCod ? (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="relative flex-1">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-700 dark:text-amber-400 font-medium">€</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={tempCodAmount}
                            onChange={(e) => setTempCodAmount(parseFloat(e.target.value) || 0)}
                            className="w-full pl-6 pr-2 py-1.5 text-sm font-bold text-amber-800 dark:text-amber-300 bg-white dark:bg-slate-700 border border-amber-300 dark:border-amber-600 rounded focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            autoFocus
                          />
                        </div>
                        <button
                          onClick={() => updateCodMutation.mutate(tempCodAmount)}
                          disabled={updateCodMutation.isPending}
                          className="p-1.5 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                          title="Guardar"
                        >
                          {updateCodMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => setIsEditingCod(false)}
                          className="p-1.5 bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 rounded hover:bg-slate-300 dark:hover:bg-slate-500"
                          title="Cancelar"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <p className="text-lg font-bold text-amber-800 dark:text-amber-300 mt-1">
                        €{(localItem.cod_amount || 0).toFixed(2)}
                      </p>
                    )}
                  </div>
                )}

                {/* Logo transportista (editable) */}
                <div className="p-3 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Transportista</span>
                    {!isEditingCarrier && (
                      <button
                        onClick={() => {
                          setTempCarrierCode(carrierCode)
                          setTempMrwTimeSlot(mrwTimeSlot)
                          setIsEditingCarrier(true)
                        }}
                        className="p-1 text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-slate-200 dark:hover:bg-slate-600 rounded transition-colors"
                        title="Cambiar transportista"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {isEditingCarrier ? (
                    <div className="space-y-3">
                      <select
                        value={tempCarrierCode}
                        onChange={(e) => setTempCarrierCode(e.target.value)}
                        className="w-full px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        autoFocus
                      >
                        {CARRIERS.map(carrier => (
                          <option key={carrier.code} value={carrier.code}>
                            {carrier.name}
                          </option>
                        ))}
                      </select>

                      {/* Selector de horario MRW (dentro del formulario de edición) */}
                      {tempCarrierCode === 'mrw' && (
                        <div>
                          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase block mb-2">
                            Horario de entrega
                          </span>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setTempMrwTimeSlot('1')}
                              className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-colors ${
                                tempMrwTimeSlot === '1'
                                  ? 'bg-red-600 text-white'
                                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 hover:bg-red-50 dark:hover:bg-red-900/30'
                              }`}
                            >
                              Mañana (08:30-14:00)
                            </button>
                            <button
                              type="button"
                              onClick={() => setTempMrwTimeSlot('2')}
                              className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-colors ${
                                tempMrwTimeSlot === '2'
                                  ? 'bg-red-600 text-white'
                                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 hover:bg-red-50 dark:hover:bg-red-900/30'
                              }`}
                            >
                              Tarde (14:00-19:00)
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            // Aplicar cambio de horario MRW
                            if (tempCarrierCode === 'mrw') {
                              setMrwTimeSlot(tempMrwTimeSlot)
                            }
                            // Si cambió el transportista, guardarlo en backend
                            if (tempCarrierCode !== carrierCode) {
                              updateCarrierMutation.mutate(tempCarrierCode)
                            } else {
                              // Solo cambió el horario, cerrar sin llamar al backend
                              setIsEditingCarrier(false)
                            }
                          }}
                          disabled={updateCarrierMutation.isPending || (tempCarrierCode === carrierCode && tempMrwTimeSlot === mrwTimeSlot)}
                          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 text-sm font-medium"
                          title="Guardar"
                        >
                          {updateCarrierMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Save className="w-3.5 h-3.5" />
                              Guardar
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => setIsEditingCarrier(false)}
                          className="px-3 py-1.5 bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 rounded hover:bg-slate-300 dark:hover:bg-slate-500 text-sm"
                          title="Cancelar"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <CarrierLogo carrierCode={carrierCode} />
                      <span className="text-xs text-slate-500 uppercase tracking-wider">
                        {getServiceName(carrierCode, item.suggested_carrier?.service_type || '')}
                      </span>
                      {/* Mostrar horario seleccionado para MRW */}
                      {carrierCode === 'mrw' && (
                        <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                          {mrwTimeSlot === '1' ? 'Mañana (08:30-14:00)' : 'Tarde (14:00-19:00)'}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Productos sin empaquetar (clickeables para seleccionar) - Estilo Outvio */}
          {unpackagedProducts.length > 0 && (
            <div className="mb-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {unpackagedProducts.map((product, index) => {
                  const isSelected = selectedProductIds.includes(product.id)
                  const isLastSelected = selectedProductIds.length > 0 && selectedProductIds[selectedProductIds.length - 1] === product.id

                  return (
                    <div
                      key={product.id || index}
                      onClick={() => toggleProductSelection(product.id)}
                      className={`relative p-3 border-2 rounded-xl cursor-pointer transition-all min-h-[160px] ${
                        isSelected
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-slate-200 dark:border-slate-700 hover:border-teal-300 hover:shadow-md'
                      }`}
                    >
                      {/* Contenido normal del producto (visible cuando NO está seleccionado) */}
                      {!isSelected && (
                        <>
                          <div className="w-full h-20 mb-2 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center overflow-hidden">
                            {product.image_url ? (
                              <img
                                src={product.image_url}
                                alt={product.name}
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                                }}
                              />
                            ) : null}
                            <Package className={`w-8 h-8 text-slate-400 ${product.image_url ? 'hidden' : ''}`} />
                          </div>
                          <h4 className="font-semibold text-xs text-slate-900 dark:text-white mb-1 line-clamp-2" title={product.name}>
                            {product.name}
                          </h4>
                          <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 mb-1">
                            {product.reference || product.name}
                          </p>
                          <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
                            €{product.total?.toFixed(2) || product.price?.toFixed(2) || '0.00'}
                          </p>
                        </>
                      )}

                      {/* Caja virtual cuando está seleccionado pero NO es el último */}
                      {isSelected && !isLastSelected && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center p-3">
                          {/* Fondo con imagen y nombre del producto en opacidad */}
                          <div className="absolute inset-0 rounded-xl overflow-hidden opacity-20">
                            {product.image_url && (
                              <img src={product.image_url} alt="" className="w-full h-full object-cover" />
                            )}
                          </div>
                          <div className="absolute bottom-2 left-2 right-2 text-white/50 text-[10px] truncate">
                            {product.name}
                          </div>
                          {/* Icono de caja */}
                          <div className="relative z-10">
                            <Box className="w-14 h-14 text-white/80" />
                            <ChevronDown className="w-5 h-5 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-bounce" />
                          </div>
                        </div>
                      )}

                      {/* Caja virtual COMPLETA cuando es el último seleccionado */}
                      {isLastSelected && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center p-3">
                          {/* Fondo con imagen y nombre del producto en opacidad */}
                          <div className="absolute inset-0 rounded-xl overflow-hidden opacity-20">
                            {product.image_url && (
                              <img src={product.image_url} alt="" className="w-full h-full object-cover" />
                            )}
                          </div>
                          <div className="absolute bottom-2 left-2 right-2 text-white/50 text-[10px] truncate">
                            {product.name}
                          </div>
                          {/* Contenido principal */}
                          <div className="relative z-10 flex flex-col items-center">
                            <span className="text-white text-xs font-bold mb-1">
                              {selectedProductIds.length} PRODUCTO{selectedProductIds.length > 1 ? 'S' : ''}
                            </span>
                            <div className="relative mb-2">
                              <Box className="w-12 h-12 text-white/80" />
                              <ChevronDown className="w-4 h-4 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-bounce" />
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleCreatePackageWithSelected()
                              }}
                              disabled={isCreatingPackage}
                              className="px-3 py-1 bg-white text-teal-600 rounded font-medium text-[11px] hover:bg-teal-50 transition-colors mb-1"
                            >
                              {isCreatingPackage ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                'Cierra paquete'
                              )}
                            </button>
                            <span className="text-white/70 text-[10px]">
                              ...o selecciona más productos
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}


          {/* Productos en este pedido - Totalización */}
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Productos en este pedido ({localItem.products?.length || 0})
            </h3>
            <div className="bg-slate-50 dark:bg-slate-900/30 rounded-lg p-3">
              <div className="space-y-2">
                {localItem.products && localItem.products.length > 0 ? (
                  localItem.products.map((product, index) => (
                    <div key={product.id || index} className="flex items-center gap-3 p-2 bg-white dark:bg-slate-800 rounded-lg">
                      {/* Miniatura */}
                      <div className="w-12 h-12 flex-shrink-0 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden flex items-center justify-center">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                              (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <Package className={`w-6 h-6 text-slate-400 ${product.image_url ? 'hidden' : ''}`} />
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                          {product.name}
                        </p>
                        {product.reference && (
                          <p className="text-[10px] text-slate-400 font-mono">
                            {product.reference}
                          </p>
                        )}
                      </div>
                      {/* Cantidad y precio */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">
                          €{product.total?.toFixed(2) || '0.00'}
                        </p>
                        <p className="text-xs text-slate-500">
                          x{product.quantity}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">
                    {localItem.items_count || 1} producto{(localItem.items_count || 1) > 1 ? 's' : ''} en el pedido
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Datos de entrega y pago */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Datos de entrega - Destinatario */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4 bg-slate-100 dark:bg-slate-800 -mx-5 px-5 py-2">
                Destinatario
              </h3>

              <div className="space-y-4">
                {/* Nombre y empresa */}
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    {localItem.customer_name || 'N/A'}
                  </p>
                  {localItem.shipping_address?.company && (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {localItem.shipping_address.company}
                    </p>
                  )}
                </div>

                {/* Email */}
                {localItem.shipping_address?.email && (
                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-600 dark:text-slate-300 break-all">
                      {localItem.shipping_address.email}
                    </span>
                  </div>
                )}

                {/* Teléfono */}
                {(localItem.shipping_address?.phone_mobile || localItem.shipping_address?.phone) && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-sm text-slate-600 dark:text-slate-300">
                      {localItem.shipping_address?.phone_mobile || localItem.shipping_address?.phone}
                    </span>
                  </div>
                )}

                {/* Sin móvil: el transportista avisa de la entrega por SMS */}
                {localItem.shipping_address?.mobile_ok === false && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 dark:border-amber-900/40 dark:bg-amber-900/20">
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-amber-800 dark:text-amber-300">
                      No hay un móvil válido. El transportista avisa de la entrega por SMS:
                      conviene corregirlo antes de generar la etiqueta.
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Dirección de envío */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4 bg-slate-100 dark:bg-slate-800 -mx-5 px-5 py-2">
                Dirección de Envío
              </h3>

              <div className="space-y-2">
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  {localItem.shipping_address?.street || '-'}
                </p>
                {localItem.shipping_address?.street2 && (
                  <p className="text-sm text-slate-700 dark:text-slate-300">
                    {localItem.shipping_address.street2}
                  </p>
                )}
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  {localItem.shipping_address?.postal_code} {localItem.shipping_address?.city}
                </p>
                {localItem.shipping_address?.state && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {localItem.shipping_address.state}
                  </p>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <CountryFlag countryCode={localItem.shipping_address?.country_code || 'ES'} className="w-5 h-4" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {localItem.shipping_address?.country_code || 'ES'}
                  </span>
                </div>
              </div>

              <button
                onClick={() => setShowBillingAddress(!showBillingAddress)}
                className="mt-4 text-sm text-teal-600 dark:text-teal-400 hover:underline flex items-center gap-1"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${showBillingAddress ? 'rotate-180' : ''}`} />
                Dirección de facturación
              </button>
            </div>

            {/* Service Point DHL */}
            {localItem.service_point && (
              <div className="rounded-lg border-l-4 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                  <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-300 uppercase tracking-wider">Punto de recogida DHL</span>
                </div>
                <p className="text-sm font-medium text-slate-900 dark:text-white">{localItem.service_point.name}</p>
                <p className="text-sm text-slate-600 dark:text-slate-300">{localItem.service_point.address}</p>
                <p className="text-sm text-slate-600 dark:text-slate-300">{localItem.service_point.postal_code} {localItem.service_point.city} ({localItem.service_point.country})</p>
              </div>
            )}

            {/* Datos de pago y envío */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4 bg-slate-100 dark:bg-slate-800 -mx-5 px-5 py-2">
                Información del Pedido
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Referencia</p>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {item.order_reference || `F${item.order_id}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Transportista</p>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {carrierCode?.toUpperCase()} - {getServiceName(carrierCode, item.suggested_carrier?.service_type || '')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Bultos / Peso</p>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {packagesCount} bulto{packagesCount > 1 ? 's' : ''} / {weight.toFixed(2)} kg
                    </p>
                  </div>
                </div>
                {/* Observaciones */}
                {observations && (
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Observaciones</p>
                    <p className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded">
                      {observations}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Columna de acciones (derecha) */}
        <div className="w-14 bg-slate-50 dark:bg-slate-900/30 border-l border-slate-200 dark:border-slate-700 flex flex-col items-center py-4 gap-2">
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
            title="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowEditModal(true)}
            className="p-2 text-slate-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors"
            title="Editar datos de entrega"
          >
            <Pencil className="w-5 h-5" />
          </button>
          <div className="relative">
            <button
              onClick={() => setShowOptionsMenu(!showOptionsMenu)}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
              title="Más opciones"
            >
              <MoreVertical className="w-5 h-5" />
            </button>

            {/* Menú desplegable de opciones */}
            {showOptionsMenu && (
              <>
                {/* Overlay para cerrar el menú al hacer clic fuera */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowOptionsMenu(false)}
                />
                <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-50">
                  <button
                    onClick={() => {
                      setShowOptionsMenu(false)
                      if (confirm('¿Estás seguro de que quieres cancelar este envío? El pedido volverá al listado de pedidos.')) {
                        cancelShipmentMutation.mutate()
                      }
                    }}
                    disabled={cancelShipmentMutation.isPending}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 disabled:opacity-50"
                  >
                    {cancelShipmentMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                    Cancelar envío
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal de edición */}
      {showEditModal && (
        <EditDeliveryModal
          item={localItem}
          onClose={() => setShowEditModal(false)}
          onSave={(data) => updateDeliveryMutation.mutate(data)}
          isSaving={updateDeliveryMutation.isPending}
        />
      )}
    </div>
  )
}
